"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { Edit3, Trash2, Star } from "lucide-react"
import type { ILeadership } from "../../../../shared/models/three-sixty-feedback-models/Leadership"
import type { IValue } from "../../../../shared/models/three-sixty-feedback-models/Values"
import EditLeadershipModal from "../modals/EditLeadershipModal"
import Modal from "../../../../shared/components/Modal"
import MODAL_NAMES from "../../../dialogs/ModalName"
import showModalFromId from "../../../../shared/functions/ModalShow"
import { useAppContext } from "../../../../shared/functions/Context"
import EditValueModal from "../modals/EditValueModal"
import type { ITemplates } from "../../../../shared/models/three-sixty-feedback-models/Templates"
import EditTemplateModal from "../modals/EditTemplateModal"
import DeleteConfirmationModal from "../modals/DeleteConfirmationDialog"

interface IProps {
  value: (IValue | ILeadership | ITemplates)[]
  onRate?: (rating: number) => void
  inputFeaturesActive?: boolean
}

export const CriteriaBox: React.FC<IProps> = ({ value }) => {
  const { api, store } = useAppContext()
  const [selectedItem, setSelectedItem] = useState<IValue | ILeadership | ITemplates | null>(null)
  const [values, setValues] = useState<(IValue | ILeadership | ITemplates)[]>(value)
  const [selectedRatings, setSelectedRatings] = useState<{ [key: string]: number }>({})

  useEffect(() => {
    setValues(value)
  }, [value])

  const toggleEdit = (item: IValue | ILeadership | ITemplates) => {
    setSelectedItem(item)
    if (isValue(item)) {
      const value = store.value.getItemById(item.id)?.asJson
      if (value) {
        store.value.select(value)
        showModalFromId(MODAL_NAMES.THREE_SIXTY_FEEDBACK.EDIT_VALUE_MODAL)
      }
    } else if (isTemplate(item)) {
      const template = store.templates.getItemById(item.id)?.asJson
      if (template) {
        store.templates.select(template)
        showModalFromId(MODAL_NAMES.THREE_SIXTY_FEEDBACK.EDIT_TEMPLATE_MODAL)
      }
    } else {
      const leadership = store.leadership.getItemById(item.id)?.asJson
      if (leadership) {
        store.leadership.select(leadership)
        showModalFromId(MODAL_NAMES.THREE_SIXTY_FEEDBACK.EDIT_LEADERSHIP_MODAL)
      }
    }
  }

  const toggleDelete = (item: IValue | ILeadership | ITemplates) => {
    if (isValue(item)) {
      const value = store.value.getItemById(item.id)?.asJson
      if (value) {
        store.value.select(value)
        showModalFromId(MODAL_NAMES.THREE_SIXTY_FEEDBACK.DELETE_VALUE_MODAL)
      }
    } else if (isTemplate(item)) {
      const template = store.templates.getItemById(item.id)?.asJson
      if (template) {
        store.templates.select(template)
        showModalFromId(MODAL_NAMES.THREE_SIXTY_FEEDBACK.DELETE_TEMPLATE_MODAL)
      }
    } else {
      const leadership = store.leadership.getItemById(item.id)?.asJson
      if (leadership) {
        store.leadership.select(leadership)
        showModalFromId(MODAL_NAMES.THREE_SIXTY_FEEDBACK.DELETE_LEADERSHIP_MODAL)
      }
    }
  }

  const handleRating = (itemId: string, statementIndex: number, rating: number) => {
    const key = `${itemId}-${statementIndex}`
    setSelectedRatings((prev) => ({ ...prev, [key]: rating }))
  }

  function isValue(item: IValue | ILeadership | ITemplates): item is IValue {
    return "valueName" in item
  }

  function isLeadership(item: IValue | ILeadership | ITemplates): item is ILeadership {
    return "criteriaName" in item
  }

  function isTemplate(item: IValue | ILeadership | ITemplates): item is ITemplates {
    return "title" in item
  }

  function getItemName(item: IValue | ILeadership | ITemplates): string {
    if (isValue(item)) return item.valueName
    if (isLeadership(item)) return item.criteriaName
    return item.title
  }

  function getItemTypeLabel(item: IValue | ILeadership | ITemplates): string {
    if (isValue(item)) return "Company Value"
    if (isLeadership(item)) return "Leadership Criteria"
    return "Template"
  }

  return (
    <div className="space-y-6">
      {values.map((item, index) => (
        <div
          key={index}
          className="group relative bg-white rounded-2xl shadow-lg border border-slate-200 overflow-hidden hover:shadow-xl transition-all duration-300"
        >
          {/* Header Section */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6 text-white relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-blue-600/90 to-indigo-600/90"></div>
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <Star className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold">{getItemName(item)}</h3>
                  <p className="text-blue-100 text-sm">{getItemTypeLabel(item)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => toggleEdit(item)}
                  className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors duration-200 backdrop-blur-sm group"
                  title="Edit"
                >
                  <Edit3 className="h-5 w-5 text-white group-hover:scale-110 transition-transform" />
                </button>
                <button
                  onClick={() => toggleDelete(item)}
                  className="p-2 bg-white/20 hover:bg-red-500/80 rounded-lg transition-colors duration-200 backdrop-blur-sm group"
                  title="Delete"
                >
                  <Trash2 className="h-5 w-5 text-white group-hover:scale-110 transition-transform" />
                </button>
              </div>
            </div>
          </div>

          {/* Statements Section */}
          <div className="p-6 space-y-6">
            {item.statements.map((statement, statementIndex) => (
              <div
                key={statementIndex}
                className="bg-slate-50 rounded-xl p-6 border border-slate-200 hover:bg-slate-100 transition-colors duration-200"
              >
                <p className="text-slate-700 font-medium mb-4 leading-relaxed">{statement.statement}</p>

                {/* Rating Section */}
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-600">Rate this statement:</span>
                  <div className="flex items-center gap-2">
                    {Array.from({ length: 5 }).map((_, ratingIndex) => {
                      const rating = ratingIndex + 1
                      const key = `${item.id}-${statementIndex}`
                      const isSelected = selectedRatings[key] === rating

                      return (
                        <button
                          key={ratingIndex}
                          onClick={() => handleRating(item.id, statementIndex, rating)}
                          className={`w-10 h-10 rounded-lg font-semibold transition-all duration-200 hover:scale-110 ${
                            isSelected
                              ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg"
                              : "bg-white border-2 border-slate-300 text-slate-600 hover:border-blue-400 hover:text-blue-600"
                          }`}
                        >
                          {rating}
                        </button>
                      )
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Edit Modals */}
      <Modal modalId={MODAL_NAMES.THREE_SIXTY_FEEDBACK.EDIT_VALUE_MODAL}>
        <EditValueModal />
      </Modal>

      <Modal modalId={MODAL_NAMES.THREE_SIXTY_FEEDBACK.EDIT_TEMPLATE_MODAL}>
        <EditTemplateModal />
      </Modal>

      <Modal modalId={MODAL_NAMES.THREE_SIXTY_FEEDBACK.EDIT_LEADERSHIP_MODAL}>
        <EditLeadershipModal />
      </Modal>

      {/* Delete Confirmation Modals */}
      <Modal modalId={MODAL_NAMES.THREE_SIXTY_FEEDBACK.DELETE_VALUE_MODAL}>
        <DeleteConfirmationModal itemType="value" />
      </Modal>

      <Modal modalId={MODAL_NAMES.THREE_SIXTY_FEEDBACK.DELETE_TEMPLATE_MODAL}>
        <DeleteConfirmationModal itemType="template" />
      </Modal>

      <Modal modalId={MODAL_NAMES.THREE_SIXTY_FEEDBACK.DELETE_LEADERSHIP_MODAL}>
        <DeleteConfirmationModal itemType="leadership" />
      </Modal>
    </div>
  )
}

export default CriteriaBox
